import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/auth/LoginPage';
import { MfaEnrolPage } from '@/pages/auth/MfaEnrolPage';
import { MfaChallengePage } from '@/pages/auth/MfaChallengePage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { AcceptTermsPage } from '@/pages/auth/AcceptTermsPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ComplianceHubPage } from '@/pages/compliance/ComplianceHubPage';
import { TrainingPage } from '@/pages/training/TrainingPage';
import { DocumentLibraryPage } from '@/pages/docLibrary/DocumentLibraryPage';
import { FormsListPage } from '@/pages/forms/FormsListPage';
import { FormDesignerPage } from '@/pages/forms/FormDesignerPage';
import { StudyScheduleMatrixPage } from '@/pages/studies/StudyScheduleMatrixPage';
import { ConsentFieldLayoutEditor } from '@/pages/consent/ConsentFieldLayoutEditor';
import { SubjectsPage } from '@/pages/subjects/SubjectsPage';
import { SubjectDetailPage } from '@/pages/subjects/SubjectDetailPage';
import { SubjectVisitsEsourcePage } from '@/pages/subjects/SubjectVisitsEsourcePage';
import { TeamPage } from '@/pages/team/TeamPage';
import { SitesListPage } from '@/pages/admin/SitesListPage';
import { SiteOnboardPage } from '@/pages/admin/SiteOnboardPage';
import { SiteDetailPage } from '@/pages/admin/SiteDetailPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminAuditPage } from '@/pages/admin/AdminAuditPage';
import { EisfMasterDataPage } from '@/pages/admin/eisf/EisfMasterDataPage';
import { DocumentTypeFormPage } from '@/pages/admin/eisf/DocumentTypeFormPage';
import { BinderTemplatesListPage } from '@/pages/admin/binderTemplates/BinderTemplatesListPage';
import { NewBinderTemplatePage } from '@/pages/admin/binderTemplates/NewBinderTemplatePage';
import { BinderTemplateEditorPage } from '@/pages/admin/binderTemplates/BinderTemplateEditorPage';
import { FieldTemplatesPage } from '@/pages/admin/FieldTemplatesPage';
import { DoaResponsibilitiesPage } from '@/pages/admin/doa/DoaResponsibilitiesPage';
import { SponsorsPage } from '@/pages/admin/SponsorsPage';
import { StudyPortfolioPage } from '@/pages/studies/StudyPortfolioPage';
import { StudyOnboardingWizardPage } from '@/pages/studies/StudyOnboardingWizardPage';
import { StudyWorkspaceHubPage } from '@/pages/studies/StudyWorkspaceHubPage';
import { VisitCalendarPage } from '@/pages/visits/VisitCalendarPage';
import { VisitDetailPage } from '@/pages/visits/VisitDetailPage';
import BinderListPage from './pages/binder/BinderListPage';
import BinderDetailPage from './pages/binder/BinderDetailPage';
import DoaPage from './pages/binder/doa/DoaPage';
import DoaGridEditor from './pages/binder/doa/DoaGridEditor';
import DelegationsIndexPage from './pages/delegations/DelegationsIndexPage';
import ApprovalNeededPage from './pages/approvals/ApprovalNeededPage';
import EisfSearchPage from './pages/search/EisfSearchPage';
import RegulatoryTagsPage from './pages/admin/RegulatoryTagsPage';
import RbacConfigurationPage from './pages/admin/RbacConfigurationPage';
import { BlindingPage } from './pages/admin/BlindingPage';
import { SubjectOnboardingWizardPage } from '@/pages/subjects/onboarding/SubjectOnboardingWizardPage';
import { SignatureQueuePage } from '@/pages/signatureQueue/SignatureQueuePage';
import { RemoteConsentPage } from '@/pages/consent/RemoteConsentPage';
import { StaffConsentPage } from '@/pages/consent/StaffConsentPage';
import { DoaSignPage } from '@/pages/doa/DoaSignPage';
import ExternalGrantLandingPage from '@/pages/external/ExternalGrantLandingPage';
import ExternalBinderPage from '@/pages/external/ExternalBinderPage';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage';
import { TermsPage } from '@/pages/legal/TermsPage';
import { CookiesPage } from '@/pages/legal/CookiesPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { MonitorAssignmentsPage } from '@/pages/admin/MonitorAssignmentsPage';
import { SiteDocumentationPage } from '@/pages/siteUser/SiteDocumentationPage';
import { StaffDocumentationPage } from '@/pages/siteUser/StaffDocumentationPage';
import { FinancialOverviewPage } from '@/pages/finance/FinancialOverviewPage';
import { QueriesPage } from '@/pages/compliance/QueriesPage';
import type { PageKey } from '@/lib/permissions';

function ProtectedRoute() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * Phase 4 (1 Oct 2026) — one guard for every role-gated page. Which roles may
 * open which page lives in lib/permissions.ts (PAGE_ROLES / canSeePage), the
 * same list the sidebar reads, so a link is never shown for a page the server
 * will refuse. The server still enforces everything, including which studies
 * the user is on; this guard only decides whether the page loads at all, so a
 * bookmarked or emailed URL lands on the dashboard instead of a 403.
 */
function PageRoute({ page }: { page: PageKey }) {
  const { user } = useAuth();
  const perms = useMyPermissions();
  if (!user) return <Navigate to="/login" replace />;
  // Same decision as the sidebar, from the same source: the RBAC grid for
  // migrated modules, the legacy table for the rest. Keeping both on
  // isVisible() is what stops the menu and the route disagreeing.
  if (!perms.isVisible(page)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/**
 * Phase 7A — /esource landing.
 * Was a `<ComingSoonPage feature="eSource (Epic-9)" />` placeholder. Repurposed
 * as a role-aware redirect so the sidebar link isn't a dead end:
 *   - Designers/admins land on /forms (Form Library — design surface)
 *   - Everyone else lands on /subjects (data-entry entry point — pick a subject,
 *     then its "Visits & eSource" tab)
 * When Epic-9's cross-visit task inbox arrives, replace this with the real page.
 */
const ESOURCE_DESIGNER_ROLES = new Set([
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'regulatory_specialist',
]);

function EsourceLandingRedirect() {
  const { user } = useAuth();
  const to =
    user && ESOURCE_DESIGNER_ROLES.has(user.role) ? '/forms' : '/subjects';
  return <Navigate to={to} replace />;
}

/** Routes that are valid only while a login challenge is in flight. */
function ChallengeOnlyRoute() {
  const { pendingChallenge, user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  if (!pendingChallenge) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/auth/reset-password', element: <ResetPasswordPage /> },
  { path: '/auth/accept-invite', element: <AcceptInvitePage /> },
  // Public remote-consent surface for subjects opening the emailed link.
  { path: '/consent/remote', element: <RemoteConsentPage /> },
  // Public staff co-sign page — PIN-gated access to the DocuSeal staff embed.
  { path: '/consent/staff-sign', element: <StaffConsentPage /> },
  // Public DoA signing page — OTP-gated access to the DocuSeal row envelope.
  { path: '/doa/sign', element: <DoaSignPage /> },
  // Sprint-11 — External reviewer magic-link + OTP landing page. Public; mints
  // a scope-bounded session JWT stored under `dreamit2.external.session`.
  { path: '/external/grant', element: <ExternalGrantLandingPage /> },
  // Sprint-11 — Read-only binder view authenticated by the scope-bounded
  // external-review JWT. Renders OUTSIDE the AppShell so it never requires
  // an internal staff session.
  { path: '/external/binders/:binderId', element: <ExternalBinderPage /> },

  // Legal — public static content, opened in new tabs from the Sign-In page and footer.
  { path: '/legal/privacy', element: <PrivacyPolicyPage /> },
  { path: '/legal/terms', element: <TermsPage /> },
  { path: '/legal/cookies', element: <CookiesPage /> },
  {
    element: <ChallengeOnlyRoute />,
    children: [
      { path: '/auth/mfa/enrol', element: <MfaEnrolPage /> },
      { path: '/auth/mfa/challenge', element: <MfaChallengePage /> },
      // F3 - reached only via a step='terms_acceptance_required' challenge.
      { path: '/auth/accept-terms', element: <AcceptTermsPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/', element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/me/profile', element: <ProfilePage /> },
      { path: '/signature-queue', element: <SignatureQueuePage /> },
      { path: '/approvals', element: <ApprovalNeededPage /> },
      { path: '/reports', element: <Navigate to="/compliance" replace /> },
      { path: '/document-library', element: <DocumentLibraryPage /> },
      // Sprint-19 — placeholders so Activation-page readiness deep-links do not 404.
      { path: '/econsent', element: <ComingSoonPage feature="eConsent management (Epic-8)" /> },
      { path: '/ip', element: <ComingSoonPage feature="Investigational Product (Epic-10)" /> },
      { path: '/studies/:studyId/schedule', element: <StudyScheduleMatrixPage /> },
      { path: '/consent-documents/:consentDocumentId/field-layout', element: <ConsentFieldLayoutEditor /> },
      // Role-gated pages — see PageRoute above and PAGE_ROLES in lib/permissions.ts.
      // F5 - site_user whitelists: content is admin-curated, low-sensitivity and
      // audit-logged; not shown to the platform admins (no site content).
      { element: <PageRoute page="site-documentation" />, children: [{ path: '/site-documentation', element: <SiteDocumentationPage /> }] },
      { element: <PageRoute page="staff-documentation" />, children: [{ path: '/staff-documentation', element: <StaffDocumentationPage /> }] },
      { element: <PageRoute page="studies" />, children: [{ path: '/studies', element: <StudyPortfolioPage /> }] },
      { element: <PageRoute page="studies.create" />, children: [{ path: '/studies/new', element: <StudyOnboardingWizardPage /> }] },
      { element: <PageRoute page="studies.onboarding" />, children: [{ path: '/studies/:id/onboarding', element: <StudyOnboardingWizardPage /> }] },
      { element: <PageRoute page="studies.workspace" />, children: [{ path: '/studies/:id', element: <StudyWorkspaceHubPage /> }] },
      {
        element: <PageRoute page="visits" />,
        children: [
          { path: '/visits', element: <VisitCalendarPage /> },
          { path: '/visits/:id', element: <VisitDetailPage /> },
          { path: '/studies/:id/visits', element: <VisitCalendarPage /> },
        ],
      },
      {
        element: <PageRoute page="subjects" />,
        children: [
          { path: '/subjects', element: <SubjectsPage /> },
          { path: '/subjects/:id', element: <SubjectDetailPage /> },
          { path: '/subjects/:id/visits-esource', element: <SubjectVisitsEsourcePage /> },
          { path: '/subjects/onboarding', element: <SubjectOnboardingWizardPage /> },
        ],
      },
      {
        element: <PageRoute page="binder" />,
        children: [
          { path: '/binder', element: <BinderListPage /> },
          { path: '/binder/:binderId', element: <BinderDetailPage /> },
        ],
      },
      {
        element: <PageRoute page="doa" />,
        children: [
          { path: '/binder/:binderId/doa', element: <DoaPage /> },
          { path: '/binder/:binderId/doa/grid', element: <DoaGridEditor /> },
        ],
      },
      { element: <PageRoute page="finance" />, children: [{ path: '/finance', element: <FinancialOverviewPage /> }] },
      { element: <PageRoute page="esource" />, children: [{ path: '/esource', element: <EsourceLandingRedirect /> }] },
      { element: <PageRoute page="compliance" />, children: [{ path: '/compliance', element: <ComplianceHubPage /> }] },
      { element: <PageRoute page="delegations" />, children: [{ path: '/delegations', element: <DelegationsIndexPage /> }] },
      { element: <PageRoute page="search" />, children: [{ path: '/search', element: <EisfSearchPage /> }] },
      { element: <PageRoute page="training" />, children: [{ path: '/training', element: <TrainingPage /> }] },
      { element: <PageRoute page="team" />, children: [{ path: '/team', element: <TeamPage /> }] },
      { element: <PageRoute page="queries" />, children: [{ path: '/queries', element: <QueriesPage /> }] },
      {
        element: <PageRoute page="forms" />,
        children: [
          { path: '/forms', element: <FormsListPage /> },
          { path: '/forms/:refOrId', element: <FormDesignerPage /> },
        ],
      },
      {
        element: <PageRoute page="platform-admin" />,
        children: [
          { path: '/admin', element: <Navigate to="/admin/sites" replace /> },
          { path: '/admin/sites', element: <SitesListPage /> },
          { path: '/admin/sites/new', element: <SiteOnboardPage /> },
          { path: '/admin/sites/:id', element: <SiteDetailPage /> },
          { path: '/admin/users', element: <AdminUsersPage /> },
          { path: '/admin/audit', element: <AdminAuditPage /> },
        ],
      },
      // F4 - Time-bound monitor assignments. Site Admin grants and revokes
      // monitor access (1 Oct 2026), so the page opens for it too.
      { element: <PageRoute page="monitor-assignments" />, children: [{ path: '/admin/monitors', element: <MonitorAssignmentsPage /> }] },
      {
        // eISF catalog, DoA responsibilities and binder templates: the builders
        // (platform rows read-only for site roles; the server enforces it).
        element: <PageRoute page="build-master-data" />,
        children: [
          { path: '/admin/eisf-master-data', element: <EisfMasterDataPage /> },
          // Feedback #16 (2026-07-28) - modal replaced with dedicated routes.
          { path: '/admin/eisf-master-data/document-types/new', element: <DocumentTypeFormPage /> },
          { path: '/admin/eisf-master-data/document-types/:code/edit', element: <DocumentTypeFormPage /> },
          { path: '/admin/doa-responsibilities', element: <DoaResponsibilitiesPage /> },
          // /new MUST be listed before /:id so React Router matches it as a
          // static path rather than treating "new" as a template id.
          { path: '/admin/binder-templates', element: <BinderTemplatesListPage /> },
          { path: '/admin/binder-templates/new', element: <NewBinderTemplatePage /> },
          { path: '/admin/binder-templates/:id', element: <BinderTemplateEditorPage /> },
        ],
      },
      // Platform-owned catalogues (1 Oct 2026): Platform and Application Admin.
      { element: <PageRoute page="regulatory-tags" />, children: [{ path: '/admin/regulatory-tags', element: <RegulatoryTagsPage /> }] },
      { element: <PageRoute page="blinding" />, children: [{ path: '/admin/blinding', element: <BlindingPage /> }] },
      { element: <PageRoute page="sponsors" />, children: [{ path: '/admin/sponsors', element: <SponsorsPage /> }] },
      { element: <PageRoute page="field-templates" />, children: [{ path: '/admin/field-templates', element: <FieldTemplatesPage /> }] },
      { element: <PageRoute page="rbac" />, children: [{ path: '/admin/rbac', element: <RbacConfigurationPage /> }] },
      { path: '*', element: <ComingSoonPage feature="Not found" /> },
    ],
  },
]);
